import { Cancel, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import {
  addFdpDriverClientEmail,
  deleteDriverClientEmail,
  fetchFdpAssociatedClients,
  getfdpList,
  updateFdp,
  updateFdpAssociatedClients,
} from "../../app/features/fdp/fdpSlice";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import {
  DriverClientEmailRequest,
  updateFdpRequest,
} from "../../app/models/requestModels/fdpRequest";
import { PAGES } from "../../enums/PagesEnum";
import { EMAIL_REGEX, STATES_LIST } from "./FdpConstants";
import { FdpResponse } from "../../app/models/responseModels/loginResponse";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import Loader from "../../components/common/loader/Loader";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import {
  Add as AddIcon,
  FileUpload as FileUploadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Done as DoneIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSubmitModal } from "../../components/common/modalPopup/useSubmitModal";
import FileUploadModal from "../../components/common/modalPopup/FileUploadModal";
import { prepareAndDownloadFile } from "../../helpers/downloadFile";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormikTextField from "../../components/common/formikFields/FormikTextField";
import FormikDatePicker from "../../components/common/formikFields/FormikDatePicker";
import dayjs from "dayjs";

interface FdpGeneralDetailsProps {
  fdpSelectedRow: FdpResponse;
  setFdpSelectedRow: any;
}
function updateFdpDetails(fdpSelectedRow: FdpResponse): updateFdpRequest {
  return {
    id: fdpSelectedRow.id,
    companyName: fdpSelectedRow.companyName,
    ownerFirstName: fdpSelectedRow.ownerFirstName,
    ownerLastName: fdpSelectedRow.ownerLastName,
    email: fdpSelectedRow.email,
    primaryPhone: fdpSelectedRow.primaryPhone,
    secondaryPhone: fdpSelectedRow.secondaryPhone,
    dispatcherName: fdpSelectedRow.dispatcherName
      ? fdpSelectedRow.dispatcherName
      : "",
    pointOfContact: fdpSelectedRow.pointOfContact
      ? fdpSelectedRow.pointOfContact
      : "",
    primaryMsa: fdpSelectedRow.primaryMsa ? fdpSelectedRow.primaryMsa : "",
    secondaryMsa: fdpSelectedRow.secondaryMsa
      ? fdpSelectedRow.secondaryMsa
      : "",
    address: fdpSelectedRow.address,
    city: fdpSelectedRow.city,
    state: fdpSelectedRow.state,
    zipCode: fdpSelectedRow.zipCode,
    invoiceFrequency: fdpSelectedRow.invoiceFrequency,
    invoiceStartDate: fdpSelectedRow.invoiceStartDate
      ? new Date(fdpSelectedRow.invoiceStartDate)
      : undefined,
  };
}
function DriverEmailRequestModel(
  fdpId: number,
  clientId: number | undefined,
  emails: string[]
): DriverClientEmailRequest {
  return {
    fdpId: fdpId,
    clientId: clientId,
    emails: emails,
  };
}

const FdpGeneralDetails = ({
  fdpSelectedRow,
  setFdpSelectedRow,
}: FdpGeneralDetailsProps) => {
  const dispatch = useAppDispatch();
  // ***** States *****
  const [openContactDetail, setOpenContactDetail] = useState(true);
  const [openAssociatedDetail, setOpenAssociatedDetail] = useState(true);
  const [showEditButton, setShowEditButton] = useState(true);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [IsFieldsReadOnly, setFieldsReadOnly] = useState(true);
  const [addNewDriverEmail, setAddNewDriverEmail] = useState(false);
  const [selectedClient, setSelectedClient] = useState<number>();

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.USER
    )
  );
  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );
  const { isFdpLoading, fdpAssociatedClients, isDriverEmailLoading } =
    useSelector((state: RootState) => state.fdp);
  const IsUpdatePermission = pagePermission
    ? pagePermission.isUpdatePermission
    : false;

  const [formValues, setFormValues] = useState<updateFdpRequest>(
    updateFdpDetails(fdpSelectedRow)
  );
  const [driverEmailText, setDriverEmailText] = useState("");
  const [driverEmailError, setDriverEmailError] = useState(false);

  const fetchAssociatedClients = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchFdpAssociatedClients(fdpSelectedRow.id)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpUsersList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpUsersListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan && fdpAssociatedClients.length === 0) {
      fetchAssociatedClients();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [fdpAssociatedClients.length, fetchAssociatedClients]);

  const handleEditButton = () => {
    setShowEditButton(false);
    setShowSaveButton(true);
    setShowCancelButton(true);
    setFieldsReadOnly(false);
  };

  const handleCancelButton = () => {
    setFormValues({
      id: undefined,
      companyName: "",
      ownerFirstName: "",
      ownerLastName: "",
      email: "",
      primaryPhone: "",
      secondaryPhone: "",
      dispatcherName: "",
      pointOfContact: "",
      primaryMsa: "",
      secondaryMsa: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      invoiceFrequency: "",
      invoiceStartDate: "",
    });
    setFieldsReadOnly(true);
    setShowCancelButton(false);
    setShowSaveButton(false);
    setShowEditButton(true);
    setTimeout(() => {
      setFormValues(updateFdpDetails(fdpSelectedRow));
    }, 1);
  };

  const validationSchema = Yup.object().shape({
    companyName: Yup.string().required("Company Name is required"),
    email: Yup.string()
      .required("Email is Required")
      .matches(EMAIL_REGEX, "Invalid Email Format"),
    primaryPhone: Yup.string()
      .required("Phone is required")
      .matches(
        /^\d{3}-\d{3}-\d{4}$/,
        "Provide valid phone number, ex XXX-XXX-XXXX"
      ),
    secondaryPhone: Yup.string().matches(
      /^\d{3}-\d{3}-\d{4}$/,
      "Provide valid phone number, ex XXX-XXX-XXXX"
    ),
    address: Yup.string().required("Address is Required"),
    primaryMsa: Yup.string().required("Primary MSA is Required"),
    city: Yup.string().required("City is Required"),
    state: Yup.string().required("State is Required"),
    zipCode: Yup.string()
      .required("Zipcode is Required")
      .matches(
        /^\d{5}(?:-\d{4})?$/,
        "Provide valid zipcode, ex XXXXX or XXXXX-XXXX"
      ),
  });

  // ***** Handlers *****
  const handleUpdate = async (updateFdpDetail: updateFdpRequest) => {
    try {
      updateFdpDetail.invoiceStartDate = dayjs(
        updateFdpDetail.invoiceStartDate
      ).format("YYYY-MM-DD");
      const response = await dispatch(updateFdp(updateFdpDetail)).unwrap();
      if (response.isSuccess) {
        const fetchedResponse = await dispatch(getfdpList()).unwrap();
        setShowSaveButton(false);
        setShowCancelButton(false);
        setShowEditButton(true);
        setFieldsReadOnly(true);
        if (fetchedResponse.isSuccess) {
          const updatedFdpRowData = fetchedResponse.data.find(
            (fdp) => fdp.id === fdpSelectedRow.id
          );
          setFormValues(updateFdpDetails(updatedFdpRowData as FdpResponse));
          setFdpSelectedRow(updatedFdpRowData as FdpResponse);
        }
        toast.success(response.message!, {
          toastId: "updateUserSuccess",
        });
      } else {
        toast.error(response.message!, {
          toastId: "updateUserError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateUserException",
      });
    }
    return true;
  };

  const handleAddDriverEmail = async (clientId: number) => {
    if (validateEmail(driverEmailText)) {
      try {
        const addDriverEmail = DriverEmailRequestModel(
          fdpSelectedRow.id,
          clientId,
          [driverEmailText.trim()]
        );
        const response = await dispatch(
          addFdpDriverClientEmail(addDriverEmail)
        ).unwrap();
        if (response.isSuccess) {
          reflectChangesPostDriverEmailUpdate(clientId, response.data);
          handleCancelAddingDriverEmail();
          toast.success(response.message!, {
            toastId: "AddDriverEmailSuccess",
          });
        } else {
          handleCancelAddingDriverEmail();
          toast.error(response.message!, {
            toastId: "AddDriverEmailError",
          });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "AddDriverEmailException",
        });
      }
    }
  };

  const handleBulkAddDriverEmail = async (
    fileMappedData: any[],
    invalidData: any[]
  ) => {
    try {
      const invalidFileData = invalidData.map(({ data, error }) => ({
        Email: data.Email,
        Error: error,
      }));
      if (fileMappedData.length === 0) {
        prepareAndDownloadFile(invalidFileData, "errorDriverEmailCreation_res");
        return false;
      }
      const emails = fileMappedData
        .filter((e) => (Object.keys(e).length ? true : false))
        .map((e) => e.Email.toString().trim());
      const addDriverEmail = DriverEmailRequestModel(
        fdpSelectedRow.id,
        selectedClient,
        emails
      );
      const response = await dispatch(
        addFdpDriverClientEmail(addDriverEmail)
      ).unwrap();
      if (response.isSuccess) {
        if (invalidData.length > 0) {
          prepareAndDownloadFile(
            invalidFileData,
            "errorDriverEmailCreation_res"
          );
          toast.warning("Some emails not saved, please check the file.", {
            toastId: "AddDriverEmailException",
          });
          return true;
        }
        toast.success(response.message!, {
          toastId: "AddDriverEmailSuccess",
        });
        return true;
      } else if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "AddDriverEmailError",
        });
        if (response.data) {
          const mergedData = [
            ...invalidData.map(({ data, error }) => ({
              Email: data.Email,
              Error: error,
            })),
            ...Object.entries(response.data).map(([Email, Error]) => ({
              Email,
              Error,
            })),
          ];
          const transformedData = mergedData.map(({ Email, Error }) => ({
            Email,
            Error,
          }));
          prepareAndDownloadFile(
            transformedData,
            "errorDriverEmailCreation_res"
          );
        }
        return true;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "AddDriverEmailException",
      });
    }
    return false;
  };

  const handleDeleteDriverEmail = async (email: string, clientId: number) => {
    try {
      const deleteDriverEmail = DriverEmailRequestModel(
        fdpSelectedRow.id,
        clientId,
        [email]
      );
      const response = await dispatch(
        deleteDriverClientEmail(deleteDriverEmail)
      ).unwrap();
      if (response.isSuccess) {
        reflectChangesPostDriverEmailUpdate(clientId, response.data);
        toast.success(response.message!, {
          toastId: "DeleteDriverEmailSuccess",
        });
      } else {
        toast.error(response.message!, {
          toastId: "DeleteDriverEmailError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "DeleteDriverEmailException",
      });
    }
  };

  const reflectChangesPostDriverEmailUpdate = (clientId: number, data: []) => {
    const clientToUpdateIndex = fdpAssociatedClients.findIndex(
      (x) => x.id === clientId
    );
    if (clientToUpdateIndex !== -1) {
      // Create a new object with the updated driverEmailIds
      const updatedClient = {
        ...fdpAssociatedClients[clientToUpdateIndex],
        driverEmailIds: data,
      };
      // Create a new array with the updated client at the correct index
      const updatedClients = [
        ...fdpAssociatedClients.slice(0, clientToUpdateIndex),
        updatedClient,
        ...fdpAssociatedClients.slice(clientToUpdateIndex + 1),
      ];
      dispatch(updateFdpAssociatedClients(updatedClients));
    }
  };

  useEffect(() => {
    if (driverEmailText.trim()) {
      if (validateEmail(driverEmailText)) {
        setDriverEmailError(false);
      } else {
        setDriverEmailError(true);
      }
    }
  }, [driverEmailText, setDriverEmailError]);

  const validateEmail = (email: string) => {
    return EMAIL_REGEX.test(email);
  };

  const handleCancelAddingDriverEmail = () => {
    setAddNewDriverEmail(false);
    setDriverEmailError(false);
    setDriverEmailText("");
  };
  const closeDriverEmailPopup = (popupState: any) => {
    popupState.close();
    handleCancelAddingDriverEmail();
  };

  const handleImportClick = (clientId: number) => {
    setSelectedClient(clientId);
    openFileUploadModal();
  };

  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        expectedColumns={[
          {
            name: "Email",
            mandatory: true,
            type: "string",
            regex: EMAIL_REGEX,
            camelCaseName: "Email",
          },
        ]}
        asyncSubmitOperation={handleBulkAddDriverEmail}
        onCompleted={fetchAssociatedClients}
        sampleFileName={"BulkDriverEmailTemplate.xlsx"}
      />
    )
  );

  if (isFdpLoading) {
    return (
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: "100vh" }}
      >
        <Loader isLoading={isFdpLoading} />
      </Grid>
    );
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Formik
          initialValues={{ ...formValues }}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={(values: updateFdpRequest, { setSubmitting }) => {
            setSubmitting(true);
            handleUpdate(values);
            setSubmitting(false);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="flex-start"
              >
                <Grid item>
                  {showEditButton && IsUpdatePermission && IsAdmin && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleEditButton}
                    >
                      Edit
                    </Button>
                  )}
                  {showSaveButton && IsUpdatePermission && IsAdmin && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mr: 1 }}
                      type="submit"
                    >
                      Save
                    </Button>
                  )}
                  {showCancelButton && IsUpdatePermission && IsAdmin && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleCancelButton}
                    >
                      Cancel
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="id"
                    name="id"
                    label="Fdp ID"
                    size="small"
                    fullWidth
                    variant="filled"
                    InputProps={{
                      readOnly: IsFieldsReadOnly ? true : true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="companyName"
                    name="companyName"
                    label="Company Name"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    required={!IsFieldsReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="dispatcherName"
                    name="dispatcherName"
                    label="Dispatcher Name"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="primaryMsa"
                    name="primaryMsa"
                    label="Primary MSA"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    required={!IsFieldsReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="secondaryMsa"
                    name="secondaryMsa"
                    label="Secondary MSA"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="address"
                    name="address"
                    label="Address"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    required={!IsFieldsReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    name="city"
                    id="city"
                    label="City"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    required={!IsFieldsReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    select
                    margin="normal"
                    id="state"
                    name="state"
                    label="State"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    required={!IsFieldsReadOnly}
                  >
                    {STATES_LIST.map((stateOption) => (
                      <MenuItem key={stateOption} value={stateOption}>
                        {stateOption}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    name="zipCode"
                    id="zipCode"
                    label="Zipcode"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    required={!IsFieldsReadOnly}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikTextField
                    margin="normal"
                    id="invoiceFrequency"
                    label="Invoice Term"
                    name="invoiceFrequency"
                    size="small"
                    fullWidth
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: IsFieldsReadOnly,
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { style: { maxHeight: 150 } },
                      },
                    }}
                    select
                  >
                    <MenuItem value="7">7 Days</MenuItem>
                    <MenuItem value="15">15 Days</MenuItem>
                    <MenuItem value="30">30 Days</MenuItem>
                  </FormikTextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <FormikDatePicker
                    name="invoiceStartDate"
                    label="Invoice Start Date"
                    size="small"
                    margin="normal"
                    variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                    fullWidth
                    isReadOnly={IsFieldsReadOnly}
                  />
                </Grid>
              </Grid>
              <br />
              {/*Fdp Contact Detail */}
              <Accordion elevation={5} expanded={openContactDetail}>
                <AccordionSummary
                  onClick={() => setOpenContactDetail(!openContactDetail)}
                  expandIcon={
                    <>
                      <IconButton
                        onClick={() => setOpenContactDetail(!openContactDetail)}
                        sx={{ color: "white" }}
                      >
                        <ExpandMore sx={{ color: "white" }} />
                      </IconButton>
                    </>
                  }
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{
                    minHeight: "36px",
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                      margin: 0,
                    },

                    "&.Mui-expanded": {
                      minHeight: "36px",
                    },
                    backgroundColor: "primary.main",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      color: "white",
                    }}
                  >
                    <Typography>Contact Details</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
                  {/* sx={{ height: 220, overflowY: "scroll" }} */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={4} md={3} lg={3}>
                        <FormikTextField
                          margin="normal"
                          id="pointOfContact"
                          name="pointOfContact"
                          label="Point Of Contact"
                          size="small"
                          fullWidth
                          variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                          InputProps={{
                            readOnly: IsFieldsReadOnly,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={3} lg={3}>
                        <FormikTextField
                          margin="normal"
                          name="email"
                          id="email"
                          label="Email"
                          size="small"
                          fullWidth
                          variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                          InputProps={{
                            readOnly: IsFieldsReadOnly,
                          }}
                          required={!IsFieldsReadOnly}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={3} lg={3}>
                        <FormikTextField
                          margin="normal"
                          id="primaryPhone"
                          name="primaryPhone"
                          label="Primary Phone"
                          size="small"
                          fullWidth
                          variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                          InputProps={{
                            readOnly: IsFieldsReadOnly,
                          }}
                          required={!IsFieldsReadOnly}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} md={3} lg={3}>
                        <FormikTextField
                          margin="normal"
                          id="secondaryPhone"
                          name="secondaryPhone"
                          label=""
                          size="small"
                          fullWidth
                          variant={!IsFieldsReadOnly ? "outlined" : "filled"}
                          InputProps={{
                            readOnly: IsFieldsReadOnly,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Form>
          )}
        </Formik>
        <br />
        {/* Fdp Association Detail */}
        <Accordion elevation={5} expanded={openAssociatedDetail}>
          <AccordionSummary
            onClick={() => setOpenAssociatedDetail(!openAssociatedDetail)}
            expandIcon={
              <>
                <IconButton
                  onClick={() => setOpenAssociatedDetail(!openAssociatedDetail)}
                  sx={{ color: "white" }}
                >
                  <ExpandMore sx={{ color: "white" }} />
                </IconButton>
              </>
            }
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              minHeight: "36px",
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
              },

              "&.Mui-expanded": {
                minHeight: "36px",
              },
              backgroundColor: "primary.main",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                color: "white",
              }}
            >
              <Typography>Associated Clients</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
            {/* sx={{ height: 220, overflowY: "scroll" }} */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {fdpAssociatedClients.length === 0 && (
                <Typography>No Associated Clients</Typography>
              )}
              {fdpAssociatedClients.map((client, index) => (
                <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                  <Grid item xs={12} sm={4} md={3} lg={3}>
                    <TextField
                      margin="normal"
                      id={"clientName" + client.clientName}
                      label="Client Name"
                      size="small"
                      fullWidth
                      defaultValue={client.clientName}
                      variant="filled"
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={3}>
                    <TextField
                      margin="normal"
                      id={"clientId" + client.id}
                      label="Client ID"
                      size="small"
                      fullWidth
                      defaultValue={client.id}
                      variant="filled"
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                  {client.storeIds && client.storeIds.length > 0 && (
                    <>
                      <Grid item xs={12} sm={4} md={2} lg={2}>
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <PopupState
                            variant="popover"
                            popupId="stores-popup-menu"
                          >
                            {(popupState) => (
                              <>
                                <Button
                                  sx={{ height: "45px", width: "200px" }}
                                  size="large"
                                  variant="outlined"
                                  {...bindTrigger(popupState)}
                                >
                                  Stores
                                  <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                                </Button>
                                <Menu {...bindMenu(popupState)}>
                                  {client.storeIds.map((store, index) => (
                                    <MenuItem
                                      sx={{ width: "200px" }}
                                      key={index}
                                      onClick={popupState.close}
                                    >
                                      {store}
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </>
                            )}
                          </PopupState>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4} md={3} lg={3}>
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <PopupState
                            variant="popover"
                            popupId="driverEmail-popup-menu"
                          >
                            {(popupState) => (
                              <>
                                <Button
                                  sx={{ height: "45px", width: "300px" }}
                                  size="large"
                                  variant="outlined"
                                  {...bindTrigger(popupState)}
                                >
                                  Driver Emails
                                  <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                                </Button>
                                <Menu
                                  sx={{ height: "400px" }}
                                  {...bindMenu(popupState)}
                                  onClose={() =>
                                    closeDriverEmailPopup(popupState)
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "end",
                                    }}
                                  >
                                    {!addNewDriverEmail && (
                                      <CustomTooltip title="Add New">
                                        <IconButton
                                          onClick={() =>
                                            setAddNewDriverEmail(true)
                                          }
                                        >
                                          <AddIcon
                                            sx={{ color: "primary.main" }}
                                          />
                                        </IconButton>
                                      </CustomTooltip>
                                    )}
                                    <CustomTooltip title="Upload">
                                      <IconButton
                                        onClick={() =>
                                          handleImportClick(client.id)
                                        }
                                      >
                                        <FileUploadIcon
                                          sx={{ color: "primary.main" }}
                                        />
                                      </IconButton>
                                    </CustomTooltip>
                                    <CustomTooltip title="Close">
                                      <IconButton
                                        onClick={() =>
                                          closeDriverEmailPopup(popupState)
                                        }
                                      >
                                        <Cancel
                                          sx={{ color: "primary.main" }}
                                        />
                                      </IconButton>
                                    </CustomTooltip>
                                  </Box>
                                  {addNewDriverEmail && (
                                    <TextField
                                      onKeyDown={(e) => e.stopPropagation()}
                                      id="driverEmail"
                                      label="Driver Email"
                                      size="small"
                                      type="email"
                                      fullWidth
                                      value={driverEmailText}
                                      onChange={(event) =>
                                        setDriverEmailText(event.target.value)
                                      }
                                      variant="filled"
                                      error={driverEmailError}
                                      helperText={
                                        driverEmailError
                                          ? "Provide valid email"
                                          : ""
                                      }
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              disabled={!driverEmailText.trim()}
                                              title="Save"
                                              edge="end"
                                              onClick={() =>
                                                handleAddDriverEmail(client.id)
                                              }
                                            >
                                              <DoneIcon
                                                sx={{
                                                  mr: 1,
                                                  color: "primary.main",
                                                }}
                                              />
                                            </IconButton>
                                            <IconButton
                                              title="Cancel"
                                              edge="end"
                                              onClick={
                                                handleCancelAddingDriverEmail
                                              }
                                            >
                                              <CancelIcon
                                                sx={{ color: "primary.main" }}
                                              />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                  {isDriverEmailLoading && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <CircularProgress color="primary" />
                                    </Box>
                                  )}
                                  {client.driverEmailIds &&
                                    client.driverEmailIds.length === 0 && (
                                      <MenuItem
                                        sx={{ width: "300px" }}
                                        key={index}
                                        onClick={popupState.close}
                                      >
                                        No Emails Exists
                                      </MenuItem>
                                    )}
                                  {client.driverEmailIds.map((email, index) => (
                                    <MenuItem
                                      key={index}
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        height: "35px",
                                        width: "300px",
                                        pl: 1,
                                        pr: 0,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          overflowX: "auto",
                                          "&::-webkit-scrollbar": {
                                            display: "none",
                                          },
                                          scrollbarWidth: "none",
                                        }}
                                      >
                                        {email}
                                      </Typography>
                                      <CustomTooltip title="Delete">
                                        <IconButton
                                          onClick={() =>
                                            handleDeleteDriverEmail(
                                              email,
                                              client.id
                                            )
                                          }
                                        >
                                          <DeleteIcon
                                            sx={{ color: "primary.main" }}
                                          />
                                        </IconButton>
                                      </CustomTooltip>
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </>
                            )}
                          </PopupState>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      {fileUploadModal}
      <br />
    </>
  );
};

export default FdpGeneralDetails;
